import React, { useState, useEffect } from 'react';
import { db, storage } from '../firebaseConfig';
import { doc, updateDoc, getDoc, collection, query, where, getDocs, limit } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import MessageModal from './MessageModal';
import ImageModal from './ImageModal';
import ToggleSwitch from './ToggleSwitch';

const SellingStatus = ({ percentage }) => {
    let status = 'N/A', color = '#6c757d';
    if (percentage === null || isNaN(percentage)) {} 
    else if (percentage >= 80) { status = 'Profit'; color = 'var(--brand-success)'; }
    else if (percentage >= 50) { status = 'Average'; color = 'var(--brand-warning)'; }
    else if (percentage >= 30) { status = 'Poor'; color = '#fd7e14'; }
    else if (percentage >= 1) { status = 'Bad'; color = 'var(--brand-danger)'; }
    else { status = 'Loss'; color = '#343a40'; }
    
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span style={{ height: '15px', width: '15px', backgroundColor: color, borderRadius: '50%', border: '1px solid var(--border-primary)' }}></span>
        <span style={{ fontSize: '1rem', fontWeight: '600' }}>{status}</span>
      </div>
    );
};

const EditItemForm = ({ user, itemId, setView }) => {
    const [item, setItem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    const [itemName, setItemName] = useState('');
    const [category, setCategory] = useState('');
    const [itemDescription, setItemDescription] = useState('');
    const [size, setSize] = useState('');
    const [color, setColor] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [perUnitCostMYR, setPerUnitCostMYR] = useState(0);
    const [sellingPriceMYR, setSellingPriceMYR] = useState(0);
    const [isCostEditable, setIsCostEditable] = useState(false);
    const [soldDate, setSoldDate] = useState(null);

    const [profitMYR, setProfitMYR] = useState(0);
    const [profitPercentage, setProfitPercentage] = useState(0);
    
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [modalState, setModalState] = useState({ message: '', onConfirm: null, showCancel: false });
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState('');

    useEffect(() => {
        const fetchItemData = async () => {
            if (!itemId) return;
            setLoading(true);
            setSoldDate(null);

            const itemRef = doc(db, "inventoryItems", itemId);
            const docSnap = await getDoc(itemRef);

            if (docSnap.exists()) {
                const data = docSnap.data();
                setItem(data);
                setItemName(data.itemName || '');
                setCategory(data.category || '');
                setItemDescription(data.itemDescription || '');
                setSize(data.size || '');
                setColor(data.color || '');
                setImageUrl(data.imageUrl || '');
                setPerUnitCostMYR(data.perUnitCostMYR || 0);
                setSellingPriceMYR(data.sellingPriceMYR || 0);
                
                if (data.status === 'Sold') {
                    const salesQuery = query(collection(db, "sales"), where("childId", "==", itemId), limit(1));
                    const salesSnapshot = await getDocs(salesQuery);
                    if (!salesSnapshot.empty) {
                        setSoldDate(salesSnapshot.docs[0].data().saleDate.toDate());
                    }
                }
            } else {
                setError("Item not found.");
            }
            setLoading(false);
        };
        fetchItemData();
    }, [itemId]);

    useEffect(() => {
        if (!imageFile) { setImagePreview(imageUrl); return; }
        const objectUrl = URL.createObjectURL(imageFile);
        setImagePreview(objectUrl);
        return () => URL.revokeObjectURL(objectUrl);
    }, [imageFile, imageUrl]);

    const handleImageChange = (e) => {
        if (e.target.files[0]) { setImageFile(e.target.files[0]); }
    };

    useEffect(() => {
        const cost = parseFloat(perUnitCostMYR) || 0;
        const price = parseFloat(sellingPriceMYR) || 0;
        const newProfit = price - cost;
        setProfitMYR(newProfit);
        setProfitPercentage(cost > 0 ? (newProfit / cost) * 100 : 0);
    }, [sellingPriceMYR, perUnitCostMYR]);

    const handleUpdate = async () => {
        setIsSubmitting(true);
        setModalState({ message: '', onConfirm: null, showCancel: false });
        
        let finalImageUrl = imageUrl;
        if (imageFile) {
            const storageRef = ref(storage, `product-images/${Date.now()}-${imageFile.name}`);
            const snapshot = await uploadBytes(storageRef, imageFile);
            finalImageUrl = await getDownloadURL(snapshot.ref);
        }
        
        const itemRef = doc(db, "inventoryItems", itemId);
        try {
            const updatedData = {
                itemName,
                itemDescription,
                size,
                color,
                perUnitCostMYR: parseFloat(perUnitCostMYR) || 0,
                sellingPriceMYR: parseFloat(sellingPriceMYR) || 0,
                profitMYR,
                profitPercentage,
                imageUrl: finalImageUrl,
                lastUpdatedAt: new Date(),
                editedBy: user.displayName,
            };
            await updateDoc(itemRef, updatedData);
            setModalState({ message: "Item details have been updated successfully!", onConfirm: () => setView('dashboard'), showCancel: false });
        } catch (err) {
            console.error("Error updating item:", err);
            setModalState({ message: `Failed to update item: ${err.message}`, onConfirm: () => setModalState({ message: ''}), showCancel: false });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setModalState({
            message: "Are you sure you want to update this item's details?",
            onConfirm: handleUpdate,
            onCancel: () => setModalState({ message: '', onConfirm: null, showCancel: false }),
            showCancel: true
        });
    };

    if (loading) return <p>Loading item details...</p>;
    if (error) return <p style={{color: 'var(--brand-danger)'}}>{error}</p>;
    if (!item) return <p>Item could not be loaded.</p>;

    return (
        <>
            <MessageModal {...modalState} />
            {isImageModalOpen && <ImageModal imageUrl={imagePreview} onClose={() => setIsImageModalOpen(false)} />}
            <form onSubmit={handleSubmit} className="product-form-container">
                <div className="product-form-main">
                    <div className="form-section">
                        <h3>Item Details for {item.childId}</h3>
                        <div className="form-grid">
                            <div className="form-field"><label>Item Name</label><input type="text" value={itemName} onChange={(e) => setItemName(e.target.value)} required /></div>
                            <div className="form-field"><label>Category</label><input type="text" value={category} readOnly /></div>
                            <div className="form-field full-span"><label>Item Description</label><textarea value={itemDescription} onChange={(e) => setItemDescription(e.target.value)} rows="3"></textarea></div>
                            <div className="form-field"><label>Size</label><input type="text" value={size} onChange={e => setSize(e.target.value)} placeholder="e.g., Medium, 12-inch" /></div>
                            <div className="form-field"><label>Color</label><input type="text" value={color} onChange={e => setColor(e.target.value)} placeholder="e.g., Gold, Royal Blue" /></div>
                            {item && item.status === 'Sold' && (
                                <div className="form-field">
                                    <label>Date Sold</label>
                                    <input 
                                        type="text" 
                                        value={soldDate ? soldDate.toLocaleDateString('en-GB') : 'Loading...'} 
                                        readOnly 
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="form-section profit-analysis-section">
                        <h3>Financials (MYR)</h3>
                        <div className="form-grid">
                            <div className="form-field">
                                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem'}}>
                                    <label style={{marginBottom: 0}}>Per Unit Cost (RM)</label>
                                    <ToggleSwitch isEnabled={isCostEditable} onToggle={() => setIsCostEditable(!isCostEditable)} label="Edit Cost" />
                                </div>
                                <input type="number" step="0.01" value={perUnitCostMYR} onChange={e => setPerUnitCostMYR(e.target.value)} readOnly={!isCostEditable} required />
                            </div>
                            <div className="form-field"><label>Planned Selling Price (RM)</label><input type="number" step="0.01" value={sellingPriceMYR} onChange={e => setSellingPriceMYR(e.target.value)} required /></div>
                            <div className="form-field"><label>Planned Profit (RM)</label><input type="text" value={profitMYR.toFixed(2)} readOnly style={{color: profitMYR < 0 ? 'var(--brand-danger)' : 'var(--brand-success)', fontWeight: 'bold'}} /></div>
                            <div className="form-field"><label>Planned Selling Status</label><SellingStatus percentage={profitPercentage} /></div>
                        </div>
                    </div>
                </div>
                <div className="product-form-sidebar">
                    <div className="form-section">
                        <h3>Product Image</h3>
                        <div className="form-field">
                            <label>Upload New Image (Optional)</label>
                            <input type="file" onChange={handleImageChange} accept="image/*" />
                        </div>
                        {imagePreview && (
                            <div className="form-field">
                                <label>Image Preview</label>
                                <div className="image-preview-box" onClick={() => setIsImageModalOpen(true)} style={{cursor: 'pointer'}}>
                                    <img src={imagePreview} alt="Preview"/>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="form-actions" style={{flexDirection: 'column', gap: '1rem'}}>
                        <button type="submit" className="submit-button" style={{width: '100%'}} disabled={isSubmitting}>{isSubmitting ? 'Updating...' : 'Update Item'}</button>
                        <button type="button" className="cancel-button" style={{width: '100%', margin: 0}} onClick={() => setView('dashboard')} disabled={isSubmitting}>Cancel</button>
                    </div>
                </div>
            </form>
        </>
    );
};

export default EditItemForm;